import { OpenApiPropertyEntry } from '@generated/models/open-api-property-entry';
import { PropertyDefinition } from '../models/property-schema';

/**
 * Backend хранит OpenAPI schema с properties как Array<{name, descriptor}> вместо dict.
 * Конвертим в legacy-dict-форму внутри утилит и UI-компонентов — там кода больше.
 */
export function descriptorEntriesToDict(
  entries: OpenApiPropertyEntry[] | null | undefined,
): Record<string, any> {
  if (!Array.isArray(entries)) return {};
  const dict: Record<string, any> = {};
  for (const e of entries) {
    if (e && typeof e.name === 'string') {
      dict[e.name] = e.descriptor ?? e;
    }
  }
  return dict;
}

function dictToDescriptorEntries(dict: Record<string, any>): Array<{ name: string; descriptor: any }> {
  return Object.entries(dict).map(([name, descriptor]) => ({ name, descriptor }));
}

/** dict значений `{name: value}` → Array<{name, value}> для значений-bag */
export function dictToValueEntries(dict: Record<string, any> | null | undefined): Array<{ name: string; value: any }> {
  if (!dict) return [];
  return Object.entries(dict).map(([name, value]) => ({ name, value }));
}

/** Array<{name, value}> → dict `{name: value}` */
export function valueEntriesToDict(entries: Array<{ name?: string; value?: any }> | null | undefined): Record<string, any> {
  if (!Array.isArray(entries)) return {};
  const dict: Record<string, any> = {};
  for (const e of entries) {
    if (e && typeof e.name === 'string') dict[e.name] = e.value;
  }
  return dict;
}

/**
 * Convert OpenAPI schema (raw JSON from DB) to PropertyDefinition array for editing.
 * Принимает schema с properties как Array<{name, descriptor}> ИЛИ legacy dict.
 */
export function openApiSchemaToProperties(schema: any): PropertyDefinition[] {
  if (!schema || typeof schema !== 'object') return [];
  const propsDict = descriptorEntriesToDict(schema.properties);
  if (!propsDict || Object.keys(propsDict).length === 0) return [];

  const properties: PropertyDefinition[] = [];

  for (const [name, propSchema] of Object.entries(propsDict)) {
    properties.push(openApiPropertyToDefinition(name, propSchema));
  }

  // Sort by x-order if available
  properties.sort((a, b) => {
    const orderA = a.order !== undefined ? a.order : 999;
    const orderB = b.order !== undefined ? b.order : 999;
    return orderA - orderB;
  });

  return properties;
}

/**
 * Convert single OpenAPI property schema to PropertyDefinition
 */
function openApiPropertyToDefinition(name: string, propSchema: any): PropertyDefinition {
  const isEnum = propSchema.enum && Array.isArray(propSchema.enum);
  const isObject = propSchema.type === 'object';
  const isArray = propSchema.type === 'array';

  let type: PropertyDefinition['type'] = 'string';
  if (isEnum) {
    type = 'enum';
  } else if (isObject) {
    type = 'object';
  } else if (isArray) {
    type = 'array';
  } else {
    type = propSchema.type || 'string';
  }

  const prop: PropertyDefinition = {
    name,
    title: propSchema.title,
    description: propSchema.description,
    type,
    nullable: propSchema.nullable || false,
    default: propSchema.default,
    isDefaultNull: propSchema.nullable && propSchema.default === null ? true : false,
    order: propSchema['x-order'],
    fillMode: propSchema['x-fill-mode'] as 'constant' | 'llm' | undefined
  };

  // Handle enum
  if (isEnum) {
    const enumNames = propSchema['x-enumNames'] || [];
    prop.enum = propSchema.enum.map((value: any, index: number) => ({
      label: enumNames[index] || String(value),
      value: value
    }));

    // Determine enum type from first value
    if (prop.enum && prop.enum.length > 0) {
      const firstValue = prop.enum[0].value;
      if (typeof firstValue === 'number') {
        prop.enumType = Number.isInteger(firstValue) ? 'integer' : 'number';
      } else {
        prop.enumType = 'string';
      }
    } else {
      prop.enumType = 'string';
    }
  }

  // Handle numeric constraints
  if (type === 'integer' || type === 'number') {
    prop.minimum = propSchema.minimum !== undefined ? propSchema.minimum : null;
    prop.maximum = propSchema.maximum !== undefined ? propSchema.maximum : null;
  }

  // Handle object type
  if (isObject && propSchema.properties) {
    prop.properties = openApiSchemaToProperties({ properties: propSchema.properties });
  }

  // Handle array type
  if (isArray) {
    const itemSchema = propSchema.items || {};
    const itemType = itemSchema.type || 'string';
    const isItemEnum = itemSchema.enum && Array.isArray(itemSchema.enum);

    prop.items = {
      type: isItemEnum ? 'enum' : itemType
    };

    if (isItemEnum) {
      const enumNames = itemSchema['x-enumNames'] || [];
      prop.items.enum = itemSchema.enum.map((value: any, index: number) => ({
        label: enumNames[index] || String(value),
        value: value
      }));

      // Determine enum type
      if (prop.items.enum && prop.items.enum.length > 0) {
        const firstValue = prop.items.enum[0].value;
        if (typeof firstValue === 'number') {
          prop.items.enumType = Number.isInteger(firstValue) ? 'integer' : 'number';
        } else {
          prop.items.enumType = 'string';
        }
      } else {
        prop.items.enumType = 'string';
      }
    }

    if (itemType === 'object' && itemSchema.properties) {
      prop.items.properties = openApiSchemaToProperties({ properties: itemSchema.properties });
    }

    prop.minItems = propSchema.minItems !== undefined ? propSchema.minItems : null;
    prop.maxItems = propSchema.maxItems !== undefined ? propSchema.maxItems : null;
  }

  return prop;
}

/**
 * Convert PropertyDefinition array to OpenAPI schema (raw JSON for DB).
 * Выдаёт schema.properties как Array<{name, descriptor}> — формат бэка.
 */
export function propertiesToOpenApiSchema(properties: PropertyDefinition[]): any {
  const dict: Record<string, any> = {};
  for (let i = 0; i < properties.length; i++) {
    const prop = properties[i];
    dict[prop.name] = propertyDefinitionToOpenApi(prop, i);
  }
  return {
    type: 'object',
    properties: dictToDescriptorEntries(dict),
  };
}

/**
 * Convert single PropertyDefinition to OpenAPI property schema
 */
function propertyDefinitionToOpenApi(prop: PropertyDefinition, order: number): any {
  const schema: any = {
    'x-order': order
  };

  // Handle enum type
  if (prop.type === 'enum' && prop.enum) {
    schema.type = prop.enumType || 'string';
    schema.enum = prop.enum.map(e => e.value);

    // Add enum names if they differ from values
    if (prop.enum.some(e => e.label !== String(e.value))) {
      schema['x-enumNames'] = prop.enum.map(e => e.label);
    }
  } else if (prop.type === 'object') {
    schema.type = 'object';
    if (prop.properties && prop.properties.length > 0) {
      const nestedSchema = propertiesToOpenApiSchema(prop.properties);
      schema.properties = nestedSchema.properties;
    }
  } else if (prop.type === 'array') {
    schema.type = 'array';

    if (prop.items) {
      schema.items = {};

      if (prop.items.type === 'enum' && prop.items.enum) {
        schema.items.type = prop.items.enumType || 'string';
        schema.items.enum = prop.items.enum.map(e => e.value);

        if (prop.items.enum.some(e => e.label !== String(e.value))) {
          schema.items['x-enumNames'] = prop.items.enum.map(e => e.label);
        }
      } else if (prop.items.type === 'object' && prop.items.properties) {
        schema.items.type = 'object';
        const nestedSchema = propertiesToOpenApiSchema(prop.items.properties);
        schema.items.properties = nestedSchema.properties;
      } else {
        schema.items.type = prop.items.type;
      }
    }

    if (prop.minItems !== null && prop.minItems !== undefined) {
      schema.minItems = prop.minItems;
    }
    if (prop.maxItems !== null && prop.maxItems !== undefined) {
      schema.maxItems = prop.maxItems;
    }
  } else {
    schema.type = prop.type;
  }

  if (prop.nullable) {
    schema.nullable = true;
  }

  if (prop.title) {
    schema.title = prop.title;
  }

  if (prop.description) {
    schema.description = prop.description;
  }

  if (prop.fillMode) {
    schema['x-fill-mode'] = prop.fillMode;
  }

  // Add default value if not in LLM mode
  if (prop.fillMode !== 'llm') {
    if (prop.isDefaultNull) {
      // Explicitly set default to null when isDefaultNull is true
      schema.default = null;
    } else if (prop.default !== undefined) {
      schema.default = prop.default;
    }
  }

  // Add numeric constraints
  if ((prop.type === 'integer' || prop.type === 'number')) {
    if (prop.minimum !== null && prop.minimum !== undefined) {
      schema.minimum = prop.minimum;
    }
    if (prop.maximum !== null && prop.maximum !== undefined) {
      schema.maximum = prop.maximum;
    }
  }

  return schema;
}
